import { CloneProposalDialog } from '@/features/proposals/components/pme/clone-proposal.dialog';
import {
  useCloneGroupLifeProposal,
  useRejectGroupLifeProposalAnalysis,
} from '@/features/proposals/hooks/group-life';
import { usePmeDocusignSignaturesStatus } from '@/features/proposals/hooks/pme/use-pme-docusign-signatures.hook';
import { useApprovePmeProposalAnalysis } from '../hooks/use-approve-pme-proposal-analysis.hook';
import {
  canCloneGroupLifeProposal,
  PortalStatus,
} from '@/shared/enums/portal-status.enum';
import { toaster } from '@/shared/ui/toaster.component';
import { GroupLifeClonePendingError } from '@/shared/utils/group-life-clone-idempotency.util';
import { Button, Field, Text } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Copy, XCircle } from 'lucide-react';
import { useState } from 'react';

interface PmeProposalAnalysisActionsProps {
  proposalId: string;
  statusPortal?: string | number | null;
  /** Proposta cancelada: as ações de análise somem; clonar permanece. */
  readOnly?: boolean;
}

export function PmeProposalAnalysisActions({
  proposalId,
  statusPortal,
  readOnly = false,
}: PmeProposalAnalysisActionsProps) {
  const navigate = useNavigate();
  const { approve, isApproving } = useApprovePmeProposalAnalysis(proposalId);
  const rejectAnalysis = useRejectGroupLifeProposalAnalysis();
  const cloneProposal = useCloneGroupLifeProposal();
  const signaturesQuery = usePmeDocusignSignaturesStatus(proposalId);
  const signaturesPending =
    Number(statusPortal) === PortalStatus.ProposalInSignature &&
    signaturesQuery.isLoading;
  const canClone =
    !signaturesPending &&
    canCloneGroupLifeProposal(statusPortal, {
      allSignaturesCompleted: signaturesQuery.data?.allCompleted === true,
    });

  const [isRejecting, setIsRejecting] = useState(false);
  const [isCloneOpen, setIsCloneOpen] = useState(false);
  const [observations, setObservations] = useState('');

  const isBusy =
    isApproving || rejectAnalysis.isPending || cloneProposal.isPending;

  const cancelReject = () => {
    setIsRejecting(false);
    setObservations('');
  };

  /**
   * Aprovar devolve a proposta ao corretor em "Proposta Aceita" (7) — a
   * assinatura é disparada pelo Confirmar dele, no Resumo.
   *
   * Falha mantém o subscritor NESTA tela e a proposta como estava, em análise.
   */
  const handleApprove = () => {
    if (isBusy) return;
    void (async () => {
      const outcome = await approve();

      if (outcome.kind === 'failed') {
        toaster.create({
          type: 'error',
          title: 'Erro ao aprovar',
          description: outcome.message,
        });
        return;
      }

      toaster.create({
        type: 'success',
        title: 'Proposta aprovada',
        description: 'A proposta voltou para o corretor concluir a assinatura.',
      });
      void navigate({ to: '/proposal-analyses' });
    })();
  };

  const handleCloneConfirm = () => {
    if (cloneProposal.isPending) return;
    cloneProposal.mutate(
      { policyId: proposalId },
      {
        onSuccess: data => {
          setIsCloneOpen(false);
          toaster.create({
            type: 'success',
            title: 'Proposta clonada',
            description: `Nova proposta Nº ${data.proposalNumber} criada em digitação. O plano da clonada é bloqueado.`,
          });
          void navigate({
            to: '/proposals/$id/pme/edit',
            params: { id: data.policyId },
          });
        },
        onError: error => {
          // O clone pode ter sido concluído sem a resposta chegar: fecha o
          // dialog para o subscritor conferir a lista em vez de confirmar de novo.
          if (error instanceof GroupLifeClonePendingError) {
            setIsCloneOpen(false);
            toaster.create({
              type: 'info',
              title: 'Clonar proposta',
              description: error.message,
            });
            return;
          }
          toaster.create({
            type: 'error',
            title: 'Erro ao clonar',
            description:
              error instanceof Error
                ? error.message
                : 'Não foi possível clonar a proposta. Tente novamente.',
          });
        },
      }
    );
  };

  const handleReject = () => {
    if (isBusy) return;
    const trimmed = observations.trim();
    if (!trimmed) return;
    rejectAnalysis.mutate(
      { policyId: proposalId, description: trimmed },
      {
        onSuccess: () => {
          toaster.create({
            type: 'success',
            title: 'Proposta recusada',
            description: 'A proposta foi recusada com o motivo registrado.',
          });
          void navigate({ to: '/proposal-analyses' });
        },
        onError: error =>
          toaster.create({
            type: 'error',
            title: 'Erro ao recusar',
            description:
              error instanceof Error
                ? error.message
                : 'Não foi possível recusar a proposta. Tente novamente.',
          }),
      }
    );
  };

  if (readOnly) {
    return (
      <div className='flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between'>
        <Button
          variant='secondary'
          size='md'
          onClick={() => void navigate({ to: '/proposal-analyses' })}
        >
          Sair
        </Button>
        {canClone && (
          <>
            <Button
              variant='secondary'
              size='md'
              disabled={cloneProposal.isPending}
              onClick={() => setIsCloneOpen(true)}
            >
              <Copy className='h-4 w-4' />
              Clonar Proposta
            </Button>
            <CloneProposalDialog
              isOpen={isCloneOpen}
              isLoading={cloneProposal.isPending}
              onClose={() => setIsCloneOpen(false)}
              onConfirm={handleCloneConfirm}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      {isRejecting && (
        <div className='flex w-full flex-col gap-4 rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6'>
          <Text className='text-base text-alba-black-500'>
            Motivo da recusa
          </Text>
          <Field.Root>
            <Field.Label>Descreva o motivo da recusa</Field.Label>
            <Field.Textarea
              value={observations}
              onChange={event => setObservations(event.target.value)}
              placeholder='Descreva o motivo da recusa...'
              rows={5}
            />
          </Field.Root>
        </div>
      )}

      <div className='flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between'>
        <Button
          variant='secondary'
          size='md'
          disabled={isBusy}
          onClick={() => {
            if (isRejecting) {
              cancelReject();
            } else {
              void navigate({ to: '/proposal-analyses' });
            }
          }}
        >
          {isRejecting ? 'Cancelar' : 'Sair'}
        </Button>

        <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
          {isRejecting ? (
            <button
              type='button'
              disabled={isBusy || !observations.trim()}
              onClick={handleReject}
              className='flex items-center justify-center gap-2 rounded-xl bg-alba-error-red-500 px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60'
            >
              <XCircle className='h-4 w-4' />
              Confirmar Recusa
            </button>
          ) : (
            <>
              {canClone && (
                <Button
                  variant='secondary'
                  size='md'
                  disabled={isBusy}
                  onClick={() => setIsCloneOpen(true)}
                >
                  <Copy className='h-4 w-4' />
                  Clonar Proposta
                </Button>
              )}
              <Button
                variant='primary'
                size='md'
                disabled={isBusy}
                onClick={() =>
                  void navigate({
                    to: '/proposal-analyses/$proposalId/pricing',
                    params: { proposalId },
                  })
                }
              >
                Tarifação
              </Button>
              <button
                type='button'
                disabled={isBusy}
                onClick={() => setIsRejecting(true)}
                className='flex items-center justify-center gap-2 rounded-xl bg-alba-error-red-500 px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60'
              >
                <XCircle className='h-4 w-4' />
                Recusar
              </button>
              <button
                type='button'
                disabled={isBusy}
                onClick={handleApprove}
                className='flex items-center justify-center gap-2 rounded-xl bg-alba-success-green-500 px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60'
              >
                <CheckCircle2 className='h-4 w-4' />
                Aprovar
              </button>
            </>
          )}
        </div>
      </div>

      <CloneProposalDialog
        isOpen={isCloneOpen}
        isLoading={cloneProposal.isPending}
        onClose={() => setIsCloneOpen(false)}
        onConfirm={handleCloneConfirm}
      />
    </div>
  );
}
